import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Check, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { 
  isNotificationSupported, 
  getNotificationPermission, 
  requestNotificationPermission,
  showGeneralNotification
} from '../services/pushNotifications';

const NotificationPermissionPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    // Check if we should show the prompt
    const checkPermission = () => {
      if (!isNotificationSupported()) {
        return;
      }

      const currentPermission = getNotificationPermission();
      setPermission(currentPermission);

      // Show prompt if permission is 'default' (not yet asked)
      // and user hasn't dismissed it in the last 2 hours (more aggressive)
      const dismissedAt = localStorage.getItem('notificationPromptDismissed');
      const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
      const shouldShow = currentPermission === 'default' && 
        (!dismissedAt || parseInt(dismissedAt) < twoHoursAgo);

      if (shouldShow) {
        // Show prompt immediately for better engagement
        setTimeout(() => setShowPrompt(true), 1500);
      }
    };

    checkPermission();
  }, []);

  const handleAllow = async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    
    if (result === 'granted') {
      // Show a test notification
      showGeneralNotification(
        'Notifications Enabled!',
        'You will now receive instant updates about bookings, messages, and more.',
        () => {}
      );
    }
    
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('notificationPromptDismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!isNotificationSupported() || permission === 'granted' || permission === 'denied') {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          className="fixed bottom-4 right-4 z-[9999] max-w-sm"
          data-testid="notification-permission-prompt"
        >
          <div className="bg-[var(--background-paper)] rounded-2xl shadow-2xl border border-[var(--border-primary)] overflow-hidden">
            {/* Animated Header */}
            <div className="bg-gradient-to-r from-[#1a365d] to-[#2d5a87] p-5 relative overflow-hidden">
              {/* Animated rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full border-2 border-white/10 animate-ping" style={{ animationDuration: '3s' }} />
              </div>
              
              <div className="relative flex items-center gap-4">
                <div className="p-3 bg-[var(--background-paper)]/20 rounded-xl backdrop-blur-sm">
                  <Bell className="w-8 h-8 text-white animate-bounce" style={{ animationDuration: '2s' }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white text-lg">Stay Connected!</h3>
                  <p className="text-white/80 text-sm">Never miss important updates</p>
                </div>
                <button 
                  onClick={handleDismiss}
                  className="p-2 hover:bg-[var(--background-paper)]/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/80" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <p className="text-[var(--text-secondary)] text-sm mb-4">
                Get instant alerts for:
              </p>
              <ul className="space-y-3 mb-5">
                <li className="flex items-center gap-3 text-sm text-[var(--text-primary)]">
                  <div className="w-6 h-6 rounded-full bg-[var(--success)]/15 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[var(--success)]" />
                  </div>
                  <span>New booking requests & confirmations</span>
                </li>
                <li className="flex items-center gap-3 text-sm text-[var(--text-primary)]">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <span>Messages from customers & workers</span>
                </li>
                <li className="flex items-center gap-3 text-sm text-[var(--text-primary)]">
                  <div className="w-6 h-6 rounded-full bg-[#F4C2C2]/15 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#F4C2C2]" />
                  </div>
                  <span>Job updates & status changes</span>
                </li>
                <li className="flex items-center gap-3 text-sm text-[var(--text-primary)]">
                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <Volume2 className="w-4 h-4 text-amber-600" />
                  </div>
                  <span>Sound alerts for urgent items</span>
                </li>
              </ul>

              <div className="flex gap-3">
                <Button
                  onClick={handleAllow}
                  className="flex-1 bg-gradient-to-r from-[#1a365d] to-[#2d5a87] hover:from-[#2d5a87] hover:to-[#1a365d] text-white font-medium py-3"
                  data-testid="allow-notifications-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Notifications
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  data-testid="dismiss-notifications-btn"
                >
                  Later
                </Button>
              </div>
              
              <p className="text-xs text-gray-400 mt-3 text-center">
                You can change this anytime in browser settings
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationPermissionPrompt;
